import { InMemoryProvider } from '@openfeature/web-sdk'

import rawFlags from '@bettermode/flags/flags.json' with { type: 'json' }

import { RuntimeConfigs, type DeployEnv } from '../../configs/runtime.js'

type FlagConfiguration = NonNullable<
  ConstructorParameters<typeof InMemoryProvider>[0]
>
type RawFlag = FlagConfiguration[string] & { alwaysDisabledIn?: string[] }

/**
 * Resolves the flag registry into an OpenFeature `InMemoryProvider` config, marking a
 * flag `disabled` in every environment listed in its `alwaysDisabledIn`.
 *
 * `disabled` (rather than an `off` variant) short-circuits evaluation and returns the
 * caller's default value with `reason: DISABLED`. Since `env` is fixed at bootstrap the
 * decision is resolved once here — no runtime `contextEvaluator`.
 *
 * Note: `disabled` returns the caller-provided default. Every Neo hook/util passes
 * `false`, so a gated flag reads `false` in the listed environments.
 */
export function getEnvGatedFlags(
  flags: Record<string, RawFlag> = rawFlags,
  env: DeployEnv = RuntimeConfigs.DEPLOY_ENV,
): FlagConfiguration {
  return Object.fromEntries(
    Object.entries(flags).map(
      ([key, { alwaysDisabledIn, ...flag }]): [string, RawFlag] => {
        const gated = env !== null && (alwaysDisabledIn?.includes(env) ?? false)
        return [key, gated ? { ...flag, disabled: true } : flag]
      },
    ),
  )
}
